import { useEffect, useRef, useState } from 'react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { assessPassword } from '@/lib/auth/passwords';
import { cn } from '@/lib/utils';

import { OtpCodeInput } from './OtpCodeInput';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';

interface ResetPasswordStepProps {
  /** Email the reset code was sent to — shown in the body copy. */
  readonly email: string;
  /** Called with the code + new password when the user submits. */
  readonly onSubmit: (code: string, newPassword: string) => Promise<void>;
  /** Called when the user clicks "Resend code". The parent re-runs ForgotPassword. */
  readonly onResend: () => Promise<void>;
  /** Inline error from the wizard (code mismatch, expired, weak password, etc.). */
  readonly topError?: string | null;
}

const RESEND_COOLDOWN_SECONDS = 30;
const CODE_LENGTH = 6;

export function ResetPasswordStep({ email, onSubmit, onResend, topError }: ResetPasswordStepProps) {
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resentToastMessage, setResentToastMessage] = useState<string | null>(null);
  // Surfaces the password rule the user is missing once they've focused the
  // field at least once. Avoids yelling "too short" the moment the meter
  // appears.
  const [passwordTouched, setPasswordTouched] = useState(false);

  // Tick the resend cooldown down to zero.
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = window.setTimeout(() => {
      setResendCooldown((c) => Math.max(0, c - 1));
    }, 1000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [resendCooldown]);

  // Clear the "code resent" confirmation after a few seconds.
  useEffect(() => {
    if (!resentToastMessage) return;
    const timer = window.setTimeout(() => {
      setResentToastMessage(null);
    }, 4000);
    return () => {
      window.clearTimeout(timer);
    };
  }, [resentToastMessage]);

  // Once the user enters the sixth digit, jump focus to the new-password
  // field — they can't auto-submit because they still need a new password,
  // but focus moves so they don't have to reach for the mouse. The ref
  // guards against re-focusing if they backspace then re-type a digit.
  const passwordFieldRef = useRef<HTMLInputElement | null>(null);
  const codeCompletedRef = useRef(false);

  const handleCodeChange = (next: string) => {
    setCode(next);
    if (next.length === CODE_LENGTH && !codeCompletedRef.current) {
      codeCompletedRef.current = true;
      passwordFieldRef.current?.focus();
    } else if (next.length < CODE_LENGTH) {
      codeCompletedRef.current = false;
    }
  };

  const passwordAssessment = assessPassword(password);
  const canSubmit =
    code.length === CODE_LENGTH && passwordAssessment.meetsRequirements && !isSubmitting;

  const submit = async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await onSubmit(code, password);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resend = async () => {
    if (resendCooldown > 0) return;
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    await onResend();
    setResentToastMessage(`Code sent to ${email}.`);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-foreground text-base font-semibold">Set a new password</h3>
        <p className="text-muted-foreground text-sm">
          We sent a {CODE_LENGTH}-digit code to{' '}
          <span className="text-foreground font-medium">{email}</span>. Enter it below and choose a
          new password.
        </p>
      </div>

      {topError ? (
        <Alert variant="destructive">
          <AlertDescription>{topError}</AlertDescription>
        </Alert>
      ) : null}

      {resentToastMessage ? (
        <Alert>
          <AlertDescription>{resentToastMessage}</AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-2">
        <Label htmlFor="reset-code-0">Verification code</Label>
        <OtpCodeInput
          id="reset-code-0"
          value={code}
          onChange={handleCodeChange}
          describedBy="reset-code-hint"
        />
        <p id="reset-code-hint" className="text-muted-foreground text-xs">
          The code expires in 24 hours. Check your spam folder if it doesn&apos;t arrive within a
          minute.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="reset-password">New password</Label>
        <Input
          id="reset-password"
          type="password"
          autoComplete="new-password"
          aria-invalid={
            passwordTouched && !passwordAssessment.meetsRequirements && password.length > 0
          }
          ref={passwordFieldRef}
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
          onBlur={() => {
            setPasswordTouched(true);
          }}
        />
        <div
          // Same reveal pattern as SignUpFormStep: grid-template-rows trick
          // animates the meter in without snapping the card height.
          // ease-[cubic-bezier(0.22,1,0.36,1)] is easeOutQuart.
          className={cn(
            'grid transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]',
            password.length >= 2 ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0',
          )}
          aria-hidden={password.length < 2}
        >
          <div className="overflow-hidden">
            <PasswordStrengthMeter assessment={passwordAssessment} />
          </div>
        </div>
      </div>

      <Button type="button" className="w-full" disabled={!canSubmit} onClick={() => void submit()}>
        {isSubmitting ? 'Resetting password…' : 'Reset password'}
      </Button>

      <p className="text-muted-foreground text-center text-xs">
        Didn&apos;t get a code?{' '}
        <button
          type="button"
          onClick={() => void resend()}
          disabled={resendCooldown > 0}
          className="text-primary hover:underline disabled:no-underline disabled:opacity-60"
        >
          {resendCooldown > 0 ? `Resend in ${String(resendCooldown)}s` : 'Resend code'}
        </button>
      </p>
    </div>
  );
}
